import React from "react";
import FontAwesomeLight from "../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import Button from "../../Buttons/Button";
import { TimePickerTrackItem } from "./TimePickerTrackItem";

type Props = {
    min: number;
    max: number;
    name: string;
    value: number;
    onChange: (name: string, value: number) => void;
};

type State = {
    target: number;
    from: number;
    step: number;
    progress: number;
    selected: number;
    dragging: boolean;
    holdDir: number;
};

const ANGLE_STEPS = 360 / 8; // Octagon

const LERP_STEPS = 10 / 300; // Take 10 steps for 300ms

type TimeoutType = ReturnType<typeof setTimeout> | null;

export default class TimePickerTrack extends React.Component<Props, State> {
    private _interpolate: TimeoutType = null;
    private _scrollTimeout: TimeoutType = null;

    private _downTimeout: TimeoutType = null;
    private _downInterval: TimeoutType = null;

    public touchY: number | null = null;
    public selectStart: number | null = null;

    constructor(props: Readonly<Props>) {
        super(props);

        const selected = this.wrapValue(props.value || 0);

        this.state = {
            target  : selected,
            from    : selected,
            step    : selected,
            progress: 0,
            dragging: false,
            holdDir : 0,
            selected,
        };
    }

    public componentDidMount(): void {
        setInterval(this.updateStep, 10);
    }

    public componentWillUnmount(): void {
        if(this._interpolate) {
            clearInterval(this._interpolate);
        }

        if(this._scrollTimeout) {
            clearTimeout(this._scrollTimeout);
        }

        if(this._downInterval) {
            clearInterval(this._downInterval);
        }

        if(this._downTimeout) {
            clearTimeout(this._downTimeout);
        }
    }

    public componentDidUpdate(prevProps: Readonly<Props>): void {
        if(
            prevProps.value !== this.props.value ||
            prevProps.max !== this.props.max ||
            prevProps.min !== this.props.min
        ) {
            // Check to see if the value update isn't caused by this component
            if(this.wrapValue(this.state.target) !== this.props.value) {
                let target = this.wrapValue(this.props.value);
                const difference = this.state.selected - target;

                // Check to see if it would be faster to wrap around bounds
                if(Math.abs(difference) > (this.props.max - this.props.min) / 2) {
                    if(difference < 0) {
                        target = this.props.min - (this.props.max - target + 1);
                    } else {
                        target = this.props.max + target + 1;
                    }
                }

                this.setState(prevState => ({
                    target  : target,
                    from    : prevState.step,
                    progress: 0,
                }));
            }
        }
    }

    public updateStep = (): void => {
        if(this.state.target !== this.state.step) {
            const { target, from } = this.state;

            this.setState(prevState => {
                const progress = prevState.progress + LERP_STEPS;
                const step = from * (1 - progress) + target * progress;

                if(Math.abs(1 - progress) < 0.01) {
                    const selected = this.wrapValue((step < 0 ? Math.ceil(target) : Math.floor(target)));

                    if(prevState.dragging) {
                        return {
                            progress,
                            selected,
                            step,
                        } as State;
                    } else {
                        return {
                            selected,
                            step    : selected,
                            target  : selected,
                            from    : selected,
                            progress: 0,
                        } as State;
                    }
                }

                return {
                    selected: this.wrapValue((step < 0 ? Math.ceil(step) : Math.floor(step))),
                    progress,
                    step,
                } as State;
            });
        }
    };

    public wrapValue = (value: number): number => {
        if(value < this.props.min) {
            return this.props.max - (this.props.min - value - 1) % (this.props.max - this.props.min);
        }

        if(value > this.props.max) {
            return this.props.min + (value - this.props.min) % (this.props.max - this.props.min + 1);
        }

        return value;
    };

    public handleMove = (dir: number): void => {
        const target = this.state.target - dir;

        this.props.onChange(this.props.name, this.wrapValue(target));

        this.setState(prevState => ({
            from    : prevState.step,
            progress: 0,
            target,
        }));
    };

    public handleDecrementHold = (): void => {
        this.prepareHold(-1);
    };

    public handleDecrement = (): void => {
        this.handleMove(-1);
    };

    public handleIncrementHold = (): void => {
        this.prepareHold(1);
    };

    public handleIncrement = (): void => {
        this.handleMove(1);
    };

    public prepareHold = (direction: number): void => {
        this._downTimeout = setTimeout(() => {
            this.setState({
                holdDir: direction,
            }, () => {
                this._downInterval = setInterval(this.handleHoldInterval, 300);
            });
        }, 200);
    };

    public handleHoldInterval = (): void => {
        this.handleMove(this.state.holdDir);
    };

    public handleRelease = (): void => {
        if(this._downTimeout) {
            clearTimeout(this._downTimeout);
        }

        if(this._downInterval) {
            clearInterval(this._downInterval);
        }

        this.setState({
            holdDir: 0,
        });
    };

    public handleTouchStart = (e: React.TouchEvent<HTMLElement>): void => {
        this.setState({
            dragging: true,
        });

        this.touchY = e.touches[0].clientY;
        this.selectStart = this.state.selected;
    };

    public handleTouchMove = (e: React.TouchEvent<HTMLElement>): void => {
        if(this.touchY === null || this.selectStart === null) {
            return;
        }

        const touchY = this.touchY;
        const selectStart = this.selectStart;

        const y = e.touches[0].clientY;

        this.setState(prevState => ({
            target  : selectStart + (touchY - y) / 100,
            from    : prevState.step,
            progress: 0,
        }));
    };

    public handleTouchEnd = (): void => {
        if(this.touchY === null || this.selectStart === null) {
            return;
        }

        const target = Math.round(this.state.target);

        this.props.onChange(this.props.name, this.wrapValue(target));

        this.setState({
            dragging: false,
            target,
        });
    };

    public handleItemClick = (value: number): void => {
        this.props.onChange(this.props.name, this.wrapValue(value));

        this.setState(prevState => ({
            target  : value,
            from    : prevState.step,
            progress: 0,
        }));
    };

    public handleWheel = (e: React.WheelEvent<HTMLDivElement>): void => {
        const delta = e.deltaY * -0.01;

        this.setState(prevState => {
            const value = prevState.step + delta;
            const selected = this.wrapValue(Math.floor(value));

            return {
                step    : this.wrapValue(value),
                target  : value,
                from    : prevState.step,
                progress: 0,
                selected,
            };
        });

        if(this._scrollTimeout) {
            clearTimeout(this._scrollTimeout);
        }

        this._scrollTimeout = setTimeout(() => {
            this.setState(prevState => {
                const value = Math.round(prevState.step);

                this.props.onChange(this.props.name, this.wrapValue(value));

                return {
                    target  : value,
                    from    : prevState.step,
                    progress: 0,
                };
            });
        }, 250);
    };

    public render(): React.ReactNode {
        const { selected, step } = this.state;

        const stepFraction = step % 1;

        const items = [];

        for(let i = -2; i < 3; i++) {
            const value = this.wrapValue(selected + i);

            items.push(
                <TimePickerTrackItem
                    key={value}
                    value={value}
                    angle={(i - stepFraction) * -ANGLE_STEPS}
                    selected={i === 0}
                    onClick={(): void => {
                        this.handleItemClick(selected + i);
                    }}
                />
            );
        }

        return (
            <div className="calendar-time-picker-track">
                <Button
                    type="button"
                    classes={[ "calendar-time-picker-track-button", "decrement" ]}
                    onMouseDown={this.handleDecrementHold}
                    onMouseUp={this.handleRelease}
                    onMouseLeave={this.handleRelease}
                    onClick={this.handleDecrement}
                >
                    <FontAwesomeLight icon="caret-up" />
                </Button>
                <div
                    className="calendar-time-picker-track-list"
                    onTouchStart={this.handleTouchStart}
                    onTouchMove={this.handleTouchMove}
                    onTouchEnd={this.handleTouchEnd}
                    onWheel={this.handleWheel}
                >
                    <div className="calendar-time-picker-track-list-slide">
                        {items}
                    </div>
                </div>
                <Button
                    type="button"
                    classes={[ "calendar-time-picker-track-button", "increment" ]}
                    onMouseDown={this.handleIncrementHold}
                    onMouseUp={this.handleRelease}
                    onMouseLeave={this.handleRelease}
                    onClick={this.handleIncrement}
                >
                    <FontAwesomeLight icon="caret-down" />
                </Button>
            </div>
        );
    }
}
